// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import './VoiceRecorder.css';

const MAX_DURATION_SECONDS = 120; // 2 minutes max

export default function VoiceRecorder({
  category,
  isProcessing,
  onProcessingStart,
  onComplete,
  onError,
  onBack,
  error,
  apiBase,
  apiHeaders,
}) {
  const [recordingState, setRecordingState] = useState('idle'); // idle | recording | recorded
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Clean up audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      stopTimer();
      stopStream();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function stopTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function stopStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer webm/opus, fall back to whatever the browser supports
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        setRecordingState('recorded');
        stopStream();
      };

      recorder.start(250); // collect chunks every 250ms
      setRecordingState('recording');
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev + 1 >= MAX_DURATION_SECONDS) {
            stopRecording();
            return MAX_DURATION_SECONDS;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      } else {
        onError('Unable to access microphone. Please check your device settings.');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = useCallback(() => {
    stopTimer();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  function handleDiscard() {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingState('idle');
    setElapsed(0);
  }

  async function handleSubmit() {
    if (!audioBlob) return;

    onProcessingStart();

    try {
      // Step 1: Request a pre-signed upload URL
      const presignRes = await axios.post(`${apiBase}/feedback/upload-url`, {
        category: category.id,
        contentType: audioBlob.type,
      }, { headers: apiHeaders });

      const { uploadUrl, feedbackId } = presignRes.data;

      // Step 2: Upload audio directly to S3
      await axios.put(uploadUrl, audioBlob, {
        headers: { 'Content-Type': audioBlob.type },
      });

      // Step 3: Trigger processing and poll for result
      const processRes = await axios.post(`${apiBase}/feedback/process`, {
        feedbackId,
        category: category.id,
      }, { headers: apiHeaders });

      onComplete(processRes.data);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Something went wrong submitting your feedback. Please try again.';
      onError(message);
    }
  }

  function formatTime(seconds) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  const progressPercent = (elapsed / MAX_DURATION_SECONDS) * 100;

  return (
    <div className="voice-recorder">
      <div className="voice-recorder__breadcrumb">
        <button className="voice-recorder__back" onClick={onBack} aria-label="Go back to category selection">
          ← Back
        </button>
        <span className="voice-recorder__category-badge">
          <span aria-hidden="true">{category.icon}</span> {category.label}
        </span>
      </div>

      <div className="voice-recorder__card">
        <h2 className="voice-recorder__heading">Record Your Feedback</h2>
        <p className="voice-recorder__instructions">
          {recordingState === 'idle' &&
            'Press record and speak clearly. Share your thoughts, suggestions, or concerns.'}
          {recordingState === 'recording' &&
            'Recording in progress — speak naturally. Press stop when finished.'}
          {recordingState === 'recorded' &&
            'Great! Review your recording below, then submit or re-record.'}
        </p>

        {permissionDenied ? (
          <div className="voice-recorder__permission-error" role="alert">
            <p>
              <strong>Microphone access denied.</strong> Please allow microphone access in your
              browser settings and reload the page.
            </p>
          </div>
        ) : (
          <>
            {/* Timer & progress */}
            {(recordingState === 'recording' || recordingState === 'recorded') && (
              <div className="voice-recorder__timer-wrap">
                <div className="voice-recorder__timer" aria-live="polite" aria-label={`Recording duration: ${formatTime(elapsed)}`}>
                  {formatTime(elapsed)}
                </div>
                <div
                  className="voice-recorder__progress"
                  role="progressbar"
                  aria-valuenow={elapsed}
                  aria-valuemin={0}
                  aria-valuemax={MAX_DURATION_SECONDS}
                >
                  <div
                    className={`voice-recorder__progress-fill${elapsed >= MAX_DURATION_SECONDS * 0.85 ? ' voice-recorder__progress-fill--warn' : ''}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="voice-recorder__max-label">Max {formatTime(MAX_DURATION_SECONDS)}</span>
              </div>
            )}

            {/* Waveform animation while recording */}
            {recordingState === 'recording' && (
              <div className="voice-recorder__waveform" aria-hidden="true">
                {Array.from({ length: 20 }).map((_, i) => (
                  <span key={i} className="voice-recorder__bar" style={{ animationDelay: `${(i * 60) % 500}ms` }} />
                ))}
              </div>
            )}

            {/* Audio playback */}
            {recordingState === 'recorded' && audioUrl && (
              <div className="voice-recorder__playback">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio controls src={audioUrl} className="voice-recorder__audio" />
              </div>
            )}

            {/* Controls */}
            <div className="voice-recorder__controls">
              {recordingState === 'idle' && (
                <button
                  className="btn btn--record"
                  onClick={startRecording}
                  aria-label="Start recording"
                >
                  <span className="btn__dot" aria-hidden="true" /> Start Recording
                </button>
              )}

              {recordingState === 'recording' && (
                <button
                  className="btn btn--stop"
                  onClick={stopRecording}
                  aria-label="Stop recording"
                >
                  <span className="btn__square" aria-hidden="true" /> Stop Recording
                </button>
              )}

              {recordingState === 'recorded' && (
                <>
                  <button
                    className="btn btn--secondary"
                    onClick={handleDiscard}
                    aria-label="Discard recording and start over"
                  >
                    Re-record
                  </button>
                  <button
                    className="btn btn--primary"
                    onClick={handleSubmit}
                    disabled={isProcessing}
                    aria-label="Submit your voice feedback"
                  >
                    {isProcessing ? (
                      <>
                        <span className="btn__spinner" aria-hidden="true" />
                        Analyzing…
                      </>
                    ) : (
                      'Submit Feedback'
                    )}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {error && (
          <div className="voice-recorder__error" role="alert">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      {isProcessing && (
        <div className="voice-recorder__processing" aria-live="polite">
          <div className="voice-recorder__processing-spinner" aria-hidden="true" />
          <p>Transcribing and analyzing your feedback…</p>
          <p className="voice-recorder__processing-sub">This may take up to 30 seconds.</p>
        </div>
      )}
    </div>
  );
}
